import React, { useRef } from 'react';
import styled from 'styled-components';

/* ─── Helpers ─── */

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function isToday(dateStr) {
  return dateStr === new Date().toISOString().split('T')[0];
}

function formatDisplayDate(dateStr, lang) {
  const d = new Date(dateStr + 'T00:00:00');
  const locale = lang === 'ta' ? 'ta-IN' : 'en-IN';
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/* ─── Styled ─── */

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing[2]}px;
  position: relative;
`;

const ArrowButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.sm}px;
  padding: ${p => p.theme.spacing[2]}px;
  cursor: pointer;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: ${p => p.theme.colors.text.primary};
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const DateDisplay = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.md}px;
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[4]}px;
  font-size: ${p => p.theme.typography.sizes.md};
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => p.theme.colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const TodayButton = styled.button`
  font-size: ${p => p.theme.typography.sizes.xs};
  color: ${p => p.theme.colors.primary};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  &:hover { color: ${p => p.theme.colors.bands?.exceptional || p.theme.colors.primary}; }
`;

const HiddenDateInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
`;

/* ─── Component ─── */

const DateNavigator = ({ value, onChange, disabled = false, lang = 'en' }) => {
  const dateInputRef = useRef(null);
  const showToday = !isToday(value);

  const openCalendar = () => {
    if (!disabled && dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const handleDateInput = (e) => {
    const selected = e.target.value;
    if (selected) {
      onChange(selected);
    }
  };

  return (
    <NavContainer>
      <ArrowButton
        onClick={() => onChange(addDays(value, -1))}
        disabled={disabled}
        aria-label="Previous day"
      >
        &#8249;
      </ArrowButton>

      <DateDisplay
        onClick={openCalendar}
        disabled={disabled}
        role="button"
        aria-label="Open calendar"
      >
        {formatDisplayDate(value, lang)}
      </DateDisplay>

      <ArrowButton
        onClick={() => onChange(addDays(value, 1))}
        disabled={disabled}
        aria-label="Next day"
      >
        &#8250;
      </ArrowButton>

      {showToday && (
        <TodayButton onClick={() => onChange(new Date().toISOString().split('T')[0])}>
          {lang === 'ta' ? 'இன்று' : 'Today'}
        </TodayButton>
      )}

      <HiddenDateInput
        ref={dateInputRef}
        type="date"
        value={value}
        onChange={handleDateInput}
        tabIndex={-1}
        aria-hidden="true"
      />
    </NavContainer>
  );
};

export default DateNavigator;
